import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useState } from "react";

import BaseEntitiesSearchAutocomplete, {
  BaseEntitiesSearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitiesSearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { noop } from "@/common/helpers/function";
import { renderIf } from "@/common/helpers/render/renderIf";
import { ApiEnumName } from "@/common/services/enum";
import { AutocompleteOptionType } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { VehicleDamageDto, VehicleDamageGetPaginatedDto } from "@/core/api/generated/v1";

import AppBadge from "../../Badge/AppBadge";
import AppIconButton from "../../Button/AppIconButton";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import DamageDetectionInline from "../DamageDetection/DamageDetectionInline";
import VehicleInline from "../Vehicle/VehicleInline";
import { vehicleDamageToOption } from "./VehicleDamageAutocomplete";
import {
  VehicleDamageAutocompleteCustomFilters,
  VehicleDamageAutocompleteCustomFiltersValues,
  computeCustomFilters,
} from "./VehicleDamageAutocompleteCustomFiltersInput";
import { VehicleDamageAutocompleteCustomFiltersInputPopover } from "./VehicleDamageAutocompleteCustomFiltersInputPopover";

const defaultDisplayProps = {
  customFilters: true,
};

export interface VehicleDamagesAutocompleteProps extends BaseEntitiesSearchAutocompleteInheritableProps<VehicleDamageDto> {
  searchFilters: {
    vehicleId: string;
  } & Partial<Omit<VehicleDamageGetPaginatedDto, "search" | "includeIds">>;
  displayProps?: Partial<typeof defaultDisplayProps>;
  defaultCustomFilters?: VehicleDamageAutocompleteCustomFiltersValues;
}

export default function VehicleDamagesAutocomplete({
  isPreload = true,
  searchFilters,
  displayProps,
  defaultCustomFilters,

  ...otherProps
}: VehicleDamagesAutocompleteProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const [customFilters, setCustomFilters] = useState<VehicleDamageAutocompleteCustomFilters | null>(
    defaultCustomFilters ? computeCustomFilters(defaultCustomFilters) : null,
  );
  const [isCustomFiltersChanged, setIsCustomFiltersChanged] = useState(false);

  // set default filters (when changed) if local filters not set yet
  if (defaultCustomFilters && (!customFilters || !customFilters.isAny)) {
    const newCustomFilters = computeCustomFilters(defaultCustomFilters);
    if (newCustomFilters.isAny) {
      setCustomFilters(newCustomFilters);
    }
  }

  return (
    <BaseEntitiesSearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={vehicleDamageToOption}
      isPreload={isPreload}
      isIgnoreCanLoadCondition={isCustomFiltersChanged} // hack to ensure refetch on custom filters change
      request={{
        requestFunc: apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          vehicleId: searchFilters.vehicleId,
          vehicleDamageGetPaginatedDto: {
            ...searchFilters,

            // prefer predefined filters over custom user filters
            partTypeId: searchFilters?.partTypeId || customFilters?.values?.partTypeId,
            damageTypeId: searchFilters?.damageTypeId || customFilters?.values?.damageTypeId,
            stateV2:
              searchFilters?.stateV2 || customFilters?.values?.states
                ? { anyOf: customFilters?.values?.states }
                : undefined,
            correctionStatus:
              searchFilters?.correctionStatus || customFilters?.values?.correctionStatus
                ? { equal: customFilters?.values?.correctionStatus }
                : undefined,
            area:
              searchFilters?.area || customFilters?.values?.area
                ? { equal: customFilters?.values?.area }
                : undefined,
            projection:
              searchFilters?.projection || customFilters?.values?.projection
                ? { equal: customFilters?.values?.projection }
                : undefined,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          vehicleDamageGetPaginatedDto: {
            ...params.vehicleDamageGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters, customFilters],
      }}
      label='Vehicle damages'
      placeholder='Search for vehicle damages...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='vehicleDamage' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography component='div' variant='body1'>
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>
                        {option.data.localNumber},{" "}
                        <InlineApiEnumValue
                          type={ApiEnumName.VehicleDamageState}
                          value={option.data.state}
                        />
                        {option.data.correctionStatus && (
                          <>
                            ,{" "}
                            <InlineApiEnumValue
                              type={ApiEnumName.DamageCorrectionStatus}
                              value={option.data.correctionStatus}
                            />
                          </>
                        )}
                        {/* <InlineApiEnumValue type='VehicleArea' value={option.data.area} />,{" "}
                                            <InlineApiEnumValue
                                              type='VehicleProjection'
                                              value={option.data.projection}
                                            /> */}
                      </Box>

                      <Box>
                        <VehicleInline
                          entity={option.data.vehicle}
                          withIcon={false}
                          withTooltip={false}
                        />
                      </Box>
                      {option.data?.damageDetection && (
                        <Box>
                          <DamageDetectionInline
                            entity={option.data?.damageDetection}
                            withIcon={false}
                            withTooltip={false}
                          />
                        </Box>
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      endAdornmentActions={{
        actions: [
          {
            if: displayProps?.customFilters,
            renderAction: (params) => (
              <VehicleDamageAutocompleteCustomFiltersInputPopover
                clickBehavior={{}}
                trigger={
                  <AppIconButton
                    {...params.iconButtonProps}
                    color={customFilters?.isAny ? "primary" : "default"}
                    title='Filters'
                  >
                    <AppIcon of='filter' />
                    {customFilters?.isAny && (
                      <AppBadge
                        sx={{ position: "absolute", top: 0, right: 0 }}
                        color='primary'
                        overlap='circular'
                        badgeContent={customFilters?.count}
                      />
                    )}
                  </AppIconButton>
                }
                subProps={{
                  searchFilters: searchFilters,
                  defaultFilters: customFilters?.values,
                  onSave: (newValue) => {
                    setIsCustomFiltersChanged(true);
                    setCustomFilters(newValue);
                  },
                  onCancel: noop,
                }}
              />
            ),
          },
        ],
      }}
      {...otherProps}
    />
  );
}
